// Copy for the three partnership pages — the hub and its two audience tracks.
//
// Localized the same way the Maple pair and the compare pages are: one
// authored file per locale, and a locale gets the pages only when its file
// exists (getContentLocales drives generateStaticParams, the hreflang set and
// the sitemap). The URL segments localize through the pathnames map in
// i18n/routing.ts — /no/partnerskap, /no/partnerskap/arbeidsplasser,
// /no/partnerskap/lokalsamfunn.
//
// Copy strings may carry <b>…</b> for inline emphasis, rendered through
// components/compare/richText.tsx.
//
// TERMINOLOGY: "family organizer" / "familieorganisering", never "family
// planner" or "familieplanlegging" — that is the standard term for
// contraception and reproductive health services in both languages, and on a
// page aimed at HR it reads as a different and more sensitive benefit.

import en from './en'
import no from './no'

export const PAGE_PATH = '/partnerships'
export const WORKPLACES_PATH = '/partnerships/workplaces'
export const COMMUNITIES_PATH = '/partnerships/communities'

/** The whole offer runs through Daisy's inbox; a form would only add a step. */
export const CONTACT_EMAIL = 'daisy@homiehub.co'
export const CONTACT_HREF = `mailto:${CONTACT_EMAIL}?subject=Homie%20partnership%20enquiry`

/** One section of body copy: the two-line marker-tape heading plus its
 *  paragraphs. `hubLink` appends a sentence linking back to the hub, used by
 *  the tracks so neither restates the trial terms. */
export interface Section {
  eyebrow: string
  highlight: string
  paragraphs: readonly string[]
  hubLink?: { before: string; label: string; after: string }
}

export interface Cta {
  heading: string
  blurb: string
  action: string
}

export interface HubContent {
  meta: { title: string; description: string }
  /** Rendered one line per entry. The break is authored, not left to the
   *  column width, so the headline keeps its shape on a wide screen. */
  h1: readonly string[]
  /** Also rendered one line per entry, for the same reason. */
  subhead: readonly string[]
  heroCta: string
  opening: Section
  fork: {
    eyebrow: string
    highlight: string
    workplaces: { label: string; line: string; examples: string }
    communities: { label: string; line: string; examples: string }
  }
  whatFamiliesGet: Section
  startSmall: Section
  models: {
    eyebrow: string
    highlight: string
    funded: { title: string; body: string }
    official: { title: string; body: string }
    closing: string
  }
  story: Section
  cta: Cta
}

export interface TrackContent {
  meta: { title: string; description: string }
  /** Authored lines, as on the hub — see HubContent above. */
  h1: readonly string[]
  subhead: readonly string[]
  heroCta: string
  /** Third crumb in the BreadcrumbList, after Home and the hub. */
  breadcrumbName: string
  sections: readonly Section[]
  cta: Cta
  /** The quiet line pointing at the sibling track. */
  crossLink: string
}

export interface PartnershipsContent {
  /** Second crumb on both tracks. */
  breadcrumbHub: string
  hub: HubContent
  workplaces: TrackContent
  communities: TrackContent
}

const CONTENT: Record<string, PartnershipsContent> = { en, no }

export function getPartnershipsContent(locale: string): PartnershipsContent | undefined {
  return CONTENT[locale]
}

/** Locales with an authored file. Adding one is a single new module. */
export function getContentLocales(): readonly string[] {
  return Object.keys(CONTENT)
}
